import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const { token, user } = useAuth();

  useEffect(() => {
    if (token && user) {
      fetchCart();
    } else {
      // Carica carrello guest da localStorage
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        setCart(JSON.parse(guestCart));
      }
    }
  }, [token, user]);

  const saveGuestCart = (cartData) => {
    localStorage.setItem('guestCart', JSON.stringify(cartData));
    setCart(cartData);
  };

  useEffect(() => {
    if (token && user) {
      fetchCart();
    }
  }, [token, user]);

  const fetchCart = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!token) {
      // Modalità guest - salva in localStorage
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        const product = response.data;
        
        const guestCart = JSON.parse(localStorage.getItem('guestCart') || '{"items":[]}');
        const existingItem = guestCart.items.find(item => item.product_id === productId);
        
        // Verifica se è un pre-ordine (stock = 0 ma con data disponibilità)
        const isPreorder = product.stock === 0 && product.availability_date;
        
        if (existingItem) {
          existingItem.quantity += quantity;
        } else {
          guestCart.items.push({
            product_id: product.id,
            name: product.name,
            price: product.price,
            quantity: quantity,
            image_url: product.image_url,
            is_preorder: isPreorder,
            availability_date: product.availability_date
          });
        }
        
        saveGuestCart(guestCart);
        return;
      } catch (error) {
        throw new Error('Errore durante l\'aggiunta al carrello');
      }
    }
    
    await axios.post(
      `${API}/cart/items`,
      { product_id: productId, quantity },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await fetchCart();
  };

  const updateCartItem = async (productId, quantity) => {
    if (!token) {
      // Modalità guest
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '{"items":[]}');
      const item = guestCart.items.find(i => i.product_id === productId);
      if (item) {
        item.quantity = quantity;
      }
      saveGuestCart(guestCart);
      return;
    }
    
    await axios.put(
      `${API}/cart/items/${productId}`,
      { product_id: productId, quantity },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await fetchCart();
  };

  const removeFromCart = async (productId) => {
    if (!token) {
      // Modalità guest
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '{"items":[]}');
      guestCart.items = guestCart.items.filter(item => item.product_id !== productId);
      saveGuestCart(guestCart);
      return;
    }
    
    await axios.delete(`${API}/cart/items/${productId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchCart();
  };

  const clearCart = async () => {
    if (!token) {
      // Modalità guest
      saveGuestCart({ items: [] });
      return;
    }
    
    await axios.delete(`${API}/cart`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchCart();
  };

  const cartTotal = cart.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;
  const cartCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        fetchCart,
        cartTotal,
        cartCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);